/**
 * MyCohortsPage — List of all cohorts the current user belongs to
 * =================================================================
 * Route: /cohorts
 *
 * Solves a real gap: a teacher running multiple classes (e.g. Architecture
 * 201 and Architecture 301) previously had no page that listed them — each
 * cohort was only reachable by typing its exact /cohort/<slug> URL from
 * memory or a bookmark. This page calls the existing, already-working
 * GET /api/cohorts endpoint (list_my_cohorts in cohort_api.py) and gives
 * it a home.
 *
 * Features:
 *   - Grid of cohort cards: name, role badge, member/project counts, visibility
 *   - "Create Cohort" button + inline creation form
 *   - Empty state for first-time users
 *   - Each card links to /cohort/:slug
 *
 * Place at: frontend/src/pages/MyCohortsPage.jsx
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Grid3X3, Globe, Lock, Building2, Plus, X,
  Loader2, GraduationCap, ChevronRight, Tag,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../api';

// ── Visibility meta (matches CohortDashboard.jsx) ──────────────────────
const VISIBILITY_ICONS = {
  public:  Globe,
  private: Lock,
  campus:  Building2,
};

const VISIBILITY_LABELS = {
  public:  'Public',
  private: 'Private',
  campus:  'Campus',
};


// ========================================================================
// CREATE COHORT FORM (inline panel)
// ========================================================================

function CreateCohortForm({ onCreated, onCancel }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Cohort name is required');
      return;
    }
    setCreating(true);
    try {
      const res = await api.post('/cohorts', {
        name: trimmed,
        description: description.trim(),
        visibility,
        taxonomy: { enabled: false, labels: [], allow_student_additions: true },
      });
      toast.success('Cohort created');
      onCreated(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create cohort');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="p-5 bg-zinc-900/50 border border-zinc-800 rounded-xl space-y-4 mb-6">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-white flex items-center gap-2">
          <GraduationCap className="w-4 h-4 text-purple-400" />
          New Cohort
        </h3>
        <button onClick={onCancel} className="text-zinc-500 hover:text-white transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div>
        <label className="block text-xs font-medium text-zinc-400 mb-1.5">Class name</label>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. Architecture 301 — Spring 2027"
          maxLength={200}
          autoFocus
          className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-purple-500/50"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-zinc-400 mb-1.5">Description (optional)</label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="What will students submit here?"
          rows={2}
          maxLength={2000}
          className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-purple-500/50 resize-none"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-zinc-400 mb-1.5">Visibility</label>
        <div className="flex gap-2">
          {['public', 'private', 'campus'].map(v => {
            const Icon = VISIBILITY_ICONS[v];
            return (
              <button
                key={v}
                type="button"
                onClick={() => setVisibility(v)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                  visibility === v
                    ? 'border-purple-500 bg-purple-500/10 text-white'
                    : 'border-zinc-700 bg-zinc-800 text-zinc-400 hover:border-zinc-600'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {VISIBILITY_LABELS[v]}
              </button>
            );
          })}
        </div>
      </div>

      <button
        onClick={handleCreate}
        disabled={creating || !name.trim()}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-500 disabled:bg-purple-600/50 disabled:text-zinc-400 text-white text-sm font-medium rounded-lg transition-colors"
      >
        {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        {creating ? 'Creating...' : 'Create Cohort'}
      </button>
    </div>
  );
}


// ========================================================================
// COHORT CARD
// ========================================================================

function CohortCard({ cohort, onClick }) {
  const VisIcon = VISIBILITY_ICONS[cohort.visibility] || Globe;
  const isAdmin = cohort.myRole === 'admin';

  return (
    <button
      onClick={onClick}
      className="group w-full text-left p-5 bg-zinc-900/50 hover:bg-zinc-800/50 border border-zinc-800 hover:border-zinc-700 rounded-xl transition-colors"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="text-base font-medium text-white leading-snug">{cohort.name}</h3>
        <span className={`flex-shrink-0 text-[10px] px-2 py-0.5 rounded-full font-medium ${
          isAdmin
            ? 'bg-purple-500/15 text-purple-300'
            : 'bg-zinc-800 text-zinc-500'
        }`}>
          {isAdmin ? 'Instructor' : 'Member'}
        </span>
      </div>

      {cohort.description && (
        <p className="text-sm text-zinc-500 mb-4 line-clamp-2">{cohort.description}</p>
      )}

      <div className="flex items-center gap-4 text-xs text-zinc-500">
        <span className="flex items-center gap-1">
          <Users className="w-3.5 h-3.5" />
          {cohort.memberCount ?? 0}
        </span>
        <span className="flex items-center gap-1">
          <Grid3X3 className="w-3.5 h-3.5" />
          {cohort.projectCount ?? 0}
        </span>
        <span className="flex items-center gap-1">
          <VisIcon className="w-3.5 h-3.5" />
          {VISIBILITY_LABELS[cohort.visibility] || cohort.visibility}
        </span>
        <ChevronRight className="w-4 h-4 ml-auto text-zinc-600 group-hover:text-zinc-400 group-hover:translate-x-0.5 transition-all" />
      </div>
    </button>
  );
}


// ========================================================================
// MAIN COMPONENT
// ========================================================================

export default function MyCohortsPage() {
  const navigate = useNavigate();

  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const fetchCohorts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/cohorts');
      setCohorts(res.data?.cohorts || []);
      setError(null);
    } catch {
      setError('Failed to load your classes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCohorts(); }, [fetchCohorts]);

  const handleCreated = (newCohort) => {
    setShowCreate(false);
    navigate(`/cohort/${newCohort.slug}`);
  };

  // Split into ones the user runs vs. ones they've just joined — instructors
  // managing several classes care most about the ones they administer.
  const taught = cohorts.filter(c => c.myRole === 'admin');
  const joined = cohorts.filter(c => c.myRole !== 'admin');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">My Classes</h1>
          <p className="text-zinc-400 text-sm">
            Every cohort you teach or belong to, in one place.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(v => !v)}
          className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium rounded-lg transition-colors flex-shrink-0"
        >
          <Plus className="w-4 h-4" />
          New Class
        </button>
      </div>

      {showCreate && (
        <CreateCohortForm
          onCreated={handleCreated}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {error && (
        <div className="text-center py-8 text-zinc-500 text-sm">{error}</div>
      )}

      {/* ── Empty state ────────────────────────────────────────────── */}
      {!error && cohorts.length === 0 && !showCreate && (
        <div className="text-center py-20">
          <GraduationCap className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-400 mb-1">You're not part of any classes yet</p>
          <p className="text-zinc-600 text-sm mb-6">
            Create one to start collecting student work, or ask your instructor for a join link.
          </p>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Your First Class
          </button>
        </div>
      )}

      {/* ── Classes you teach ──────────────────────────────────────── */}
      {taught.length > 0 && (
        <div className="mb-8">
          <h2 className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-zinc-500 mb-3">
            <Tag className="w-3.5 h-3.5" />
            Teaching ({taught.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {taught.map(c => (
              <CohortCard key={c.id} cohort={c} onClick={() => navigate(`/cohort/${c.slug}`)} />
            ))}
          </div>
        </div>
      )}

      {/* ── Classes you've joined ──────────────────────────────────── */}
      {joined.length > 0 && (
        <div>
          <h2 className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-zinc-500 mb-3">
            <Users className="w-3.5 h-3.5" />
            Joined ({joined.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {joined.map(c => (
              <CohortCard key={c.id} cohort={c} onClick={() => navigate(`/cohort/${c.slug}`)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}